import React, { useMemo, useState } from 'react';
import { GridColDef, GridRenderCellParams, GridRowsProp } from '@mui/x-data-grid';
import { Alert, Box, Button, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { DeleteOutlined, SendOutlined, SyncOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import CAppDialog from '../../../../app/components/CAppDialog';
import CAppDataGrid from '../../../../app/components/CAppDataGrid';
import AppFormatDate from '../../../../app/features/format/AppFormatDate';
import {
	useNotificationMonitoringAction,
	useNotificationMonitoringRecipient,
} from '../../hooks/useNotificationMonitoring';
import { INotificationMonitoringItem, TNotificationAction } from '../../interfaces/INotificationMonitoring';
import langAdminNotificationMonitoring from '../../langs/langAdminNotificationMonitoring';
import HAdminNotificationMonitoring from './HAdminNotificationMonitoring';

const ACTIONS: Record<TNotificationAction, { icon: React.ReactNode; label: string; help?: string }> = {
	send: {
		icon: <SendOutlined fontSize="small" />,
		label: langAdminNotificationMonitoring.action_send.tag,
		help: langAdminNotificationMonitoring.action_send_help.tag,
	},
	resynchronize: {
		icon: <SyncOutlined fontSize="small" />,
		label: langAdminNotificationMonitoring.action_resynchronize.tag,
		help: langAdminNotificationMonitoring.action_resynchronize_help.tag,
	},
	delete: {
		icon: <DeleteOutlined fontSize="small" />,
		label: langAdminNotificationMonitoring.action_delete.tag,
	},
};

// Actions changing what the investor receives are confirmed first
const CONFIRMATIONS: Partial<Record<TNotificationAction, string>> = {
	send: langAdminNotificationMonitoring.confirm_send.tag,
	delete: langAdminNotificationMonitoring.confirm_delete.tag,
};

type TPendingAction = { action: TNotificationAction; item: INotificationMonitoringItem };

type CAdminNotificationRecipientDialogProps = {
	email: string | null;
	onClose: () => void;
};

const formatDatetime = (value: string | null) => (value ? moment(value).format('DD/MM/YYYY HH:mm') : '-');

const CAdminNotificationRecipientDialog = (props: CAdminNotificationRecipientDialogProps) => {
	const { email, onClose } = props;
	const { t } = useTranslation();

	const { data: items, isLoading, error } = useNotificationMonitoringRecipient(email);
	const actionMutation = useNotificationMonitoringAction();

	const [pendingAction, setPendingAction] = useState<TPendingAction | null>(null);
	const [feedback, setFeedback] = useState<{ severity: 'success' | 'error'; message: string } | null>(null);

	// The dialog stays mounted: reset its state so nothing leaks to the next contact
	const handleClose = () => {
		setPendingAction(null);
		setFeedback(null);
		onClose();
	};

	const runAction = (action: TNotificationAction, item: INotificationMonitoringItem) => {
		setPendingAction(null);
		setFeedback(null);
		actionMutation.mutate(
			{ action, id: item.id },
			{
				onSuccess: (result) =>
					setFeedback(
						result.ok
							? { severity: 'success', message: t(langAdminNotificationMonitoring.action_done.tag) }
							: {
									severity: 'error',
									message: t(langAdminNotificationMonitoring.action_failed.tag, {
										reason:
											result.errorReason ?? t(langAdminNotificationMonitoring.unknown_reason.tag),
									}),
							  },
					),
				onError: (e) =>
					setFeedback({
						severity: 'error',
						message: t(langAdminNotificationMonitoring.action_failed.tag, {
							reason: HAdminNotificationMonitoring.errorMessage(e),
						}),
					}),
			},
		);
	};

	const requestAction = (action: TNotificationAction, item: INotificationMonitoringItem) => {
		if (CONFIRMATIONS[action]) {
			setPendingAction({ action, item });
		} else {
			runAction(action, item);
		}
	};

	const rows: GridRowsProp = useMemo(() => (items ?? []).map((x) => ({ ...x })), [items]);

	const columns: GridColDef[] = [
		{
			field: 'documentName',
			headerName: t(langAdminNotificationMonitoring.column_document.tag),
			minWidth: 200,
			flex: 1,
		},
		{
			field: 'documentReleaseDate',
			headerName: t(langAdminNotificationMonitoring.column_release_date.tag),
			width: 120,
			renderCell: (params: GridRenderCellParams) => (params.value ? <AppFormatDate date={params.value} /> : '-'),
		},
		{
			field: 'subject',
			headerName: t(langAdminNotificationMonitoring.column_subject.tag),
			minWidth: 200,
			flex: 1,
		},
		{
			field: 'statusTag',
			headerName: t(langAdminNotificationMonitoring.column_status.tag),
			width: 100,
		},
		{
			field: 'sendDatetime',
			headerName: t(langAdminNotificationMonitoring.column_send_datetime.tag),
			width: 140,
			renderCell: (params: GridRenderCellParams) => formatDatetime(params.value),
		},
		{
			field: 'trackingStatus',
			headerName: t(langAdminNotificationMonitoring.column_tracking.tag),
			width: 170,
			renderCell: (params: GridRenderCellParams) =>
				params.value ? `${params.value} (${formatDatetime(params.row.trackingStatusDatetime)})` : '-',
		},
		{
			field: 'rejectReason',
			headerName: t(langAdminNotificationMonitoring.column_reject_reason.tag),
			width: 120,
		},
		{
			field: 'actions',
			headerName: t(langAdminNotificationMonitoring.column_actions.tag),
			width: 120,
			sortable: false,
			renderCell: (params: GridRenderCellParams) => {
				const item = params.row as INotificationMonitoringItem;
				return HAdminNotificationMonitoring.availableActions(item).map((action) => {
					const { label, help } = ACTIONS[action];
					return (
						<Tooltip key={action} title={help ? `${t(label)} - ${t(help)}` : t(label)}>
							<span>
								<IconButton
									size="small"
									disabled={actionMutation.isLoading}
									onClick={() => requestAction(action, item)}
									aria-label={t(ACTIONS[action].label)}
								>
									{ACTIONS[action].icon}
								</IconButton>
							</span>
						</Tooltip>
					);
				});
			},
		},
	];

	// Delete is final, a retry may send the mail twice: make both stand out
	const confirmColor = (): 'error' | 'warning' | 'primary' => {
		if (pendingAction?.action === 'delete') {
			return 'error';
		}
		if (pendingAction && HAdminNotificationMonitoring.isRetry(pendingAction.action, pendingAction.item)) {
			return 'warning';
		}
		return 'primary';
	};

	const confirmationText = () => {
		if (!pendingAction) {
			return '';
		}
		const { action, item } = pendingAction;
		const retry = HAdminNotificationMonitoring.isRetry(action, item);
		const tag = retry ? langAdminNotificationMonitoring.confirm_retry.tag : CONFIRMATIONS[action];
		return tag ? t(tag, { subject: item.subject ?? '', email }) : '';
	};

	const renderContent = () => {
		if (error) {
			return <Alert severity="error">{HAdminNotificationMonitoring.errorMessage(error)}</Alert>;
		}
		if (isLoading) {
			return (
				<Box sx={{ textAlign: 'center', p: 3 }}>
					<CircularProgress />
				</Box>
			);
		}
		return (
			<CAppDataGrid
				className="admin-notification-monitoring-recipient-table"
				rows={rows}
				columns={columns}
				autoHeight
				pagination
			/>
		);
	};

	return (
		<>
			<CAppDialog open={!!email} onClose={handleClose} title={email ?? ''} maxWidth="xl">
				{feedback ? (
					<Alert severity={feedback.severity} sx={{ mb: 2 }} onClose={() => setFeedback(null)}>
						{feedback.message}
					</Alert>
				) : (
					''
				)}
				{renderContent()}
			</CAppDialog>
			<CAppDialog
				open={!!pendingAction}
				onClose={() => setPendingAction(null)}
				title={t(langAdminNotificationMonitoring.confirm_title.tag)}
				maxWidth="sm"
				actions={
					<>
						<Button onClick={() => setPendingAction(null)}>
							{t(langAdminNotificationMonitoring.cancel.tag)}
						</Button>
						<Button
							variant="contained"
							color={confirmColor()}
							onClick={() => pendingAction && runAction(pendingAction.action, pendingAction.item)}
						>
							{t(langAdminNotificationMonitoring.confirm.tag)}
						</Button>
					</>
				}
			>
				{confirmationText()}
			</CAppDialog>
		</>
	);
};

export default CAdminNotificationRecipientDialog;
